import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { cn, getFileExtension } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import {
  isAudioFileTyped,
  isImageFileTyped,
  isVideoFileTyped,
} from "@/react/components/entityViews/files/components/FileHelpers";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { toast } from "sonner";

export const IMAGE_SHARE_INCLUDES = [
  "Image and description",
  "Incentive points",
  "Findings / insights",
] as const;

export const DOCUMENT_SHARE_INCLUDES = [
  "Document preview",
  "File name and description",
] as const;

export const VIDEO_SHARE_INCLUDES = [
  "Video playback",
  "File name and description",
] as const;

export const AUDIO_SHARE_INCLUDES = [
  "Audio playback",
  "File name and description",
] as const;

const SHARE_INFO_CARD_CLS =
  "rounded-swivu-base border border-border/60 bg-muted/25 p-4";

function ShareIncludesList({
  intro,
  items,
  className,
}: {
  intro: string;
  items: readonly string[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      <p className="text-sm font-normal leading-snug text-foreground">{intro}</p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm font-normal text-foreground">
            <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Public share URL for the current file (`{agenda.url}/file/{id}`). */
export function useFilePublicShareLink() {
  const { selectedBoard } = useSwivuBryntumContext();
  const { file, project } = useFileDetailModal();
  const [shareLink, setShareLink] = useState("");

  useEffect(() => {
    const resourceAgendaRecord = project?.resourceAgendaStore?.first;
    if (resourceAgendaRecord && file?.id) {
      // @ts-expect-error - Bryntum model types
      const fullURL = resourceAgendaRecord?.url || "";
      setShareLink(`${fullURL}/file/${file.id}`);
    } else {
      setShareLink("");
    }
  }, [project, selectedBoard, file]);

  const copyShareLink = async () => {
    if (!shareLink) {
      toast.error("Share link is not available");
      return false;
    }
    try {
      await navigator.clipboard.writeText(shareLink);
      return true;
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link");
      return false;
    }
  };

  return { shareLink, copyShareLink };
}

interface PublicShareSectionProps {
  className?: string;
  onOpenPoints?: () => void;
  onOpenFindings?: () => void;
}

/**
 * Clarity-only: what the public link includes (copy lives in the sticky footer).
 */
export function PublicShareSection({
  className,
  onOpenPoints,
  onOpenFindings,
}: PublicShareSectionProps) {
  const { file } = useFileDetailModal();

  if (!file) return null;

  const isPDF = getFileExtension(file.name) === "pdf";
  const isImage = Boolean(isImageFileTyped(file) && !isPDF);
  const isDocument = file.fileType === "document";
  const isVideo = isVideoFileTyped(file);
  const isAudio = isAudioFileTyped(file);

  if (isDocument) {
    return (
      <ShareIncludesList
        className={cn(SHARE_INFO_CARD_CLS, className)}
        intro="Anyone with this link opens the public page for this document, including:"
        items={DOCUMENT_SHARE_INCLUDES}
      />
    );
  }

  if (isVideo) {
    return (
      <ShareIncludesList
        className={cn(SHARE_INFO_CARD_CLS, className)}
        intro="Anyone with this link opens the public page for this video, including:"
        items={VIDEO_SHARE_INCLUDES}
      />
    );
  }

  if (isAudio) {
    return (
      <ShareIncludesList
        className={cn(SHARE_INFO_CARD_CLS, className)}
        intro="Anyone with this link opens the public page for this audio file, including:"
        items={AUDIO_SHARE_INCLUDES}
      />
    );
  }

  if (!isImage) {
    return (
      <p className={cn("text-sm font-normal leading-snug text-foreground", className)}>
        Anyone with this link opens the public page for this file.
      </p>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <p className="text-sm font-normal leading-snug text-foreground">
        Anyone with this link sees the public page for this image, including:
      </p>
      <ul className="space-y-2">
        {IMAGE_SHARE_INCLUDES.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm font-normal text-foreground">
            <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {(onOpenPoints || onOpenFindings) && (
        <p className="text-sm font-normal text-muted-foreground">
          Review{" "}
          {onOpenPoints ? (
            <button
              type="button"
              className="text-foreground underline-offset-2 hover:underline"
              onClick={onOpenPoints}
            >
              Points
            </button>
          ) : (
            "Points"
          )}{" "}
          and{" "}
          {onOpenFindings ? (
            <button
              type="button"
              className="text-foreground underline-offset-2 hover:underline"
              onClick={onOpenFindings}
            >
              Findings
            </button>
          ) : (
            "Findings"
          )}{" "}
          before you share.
        </p>
      )}
    </div>
  );
}

/** Alias for popovers / callers that only need the includes list. */
export function ShareIncludesInfo({ className }: { className?: string }) {
  return <PublicShareSection className={className} />;
}
